import React from "react";
import type { ReactNode } from "react";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { Modal } from "../Modal/Modal";
import { widthTokens } from "../../designTokens";

type ErrorDialogProps = {
  show: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
  primaryButtonLabel?: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.primaryTextColor};
  margin: 0;
`;

const Subtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.secondaryTextColor};
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const Card = styled.div`
  border-radius: ${({ theme }) => theme.resolve("spacing_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  background: ${({ theme }) => theme.primaryBG};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  align-items: flex-start;
  min-width: 0;
  width: 100%;
`;

const IconWrapper = styled.div`
  display: flex;
  align-self: center;
  justify-content: flex-start;
  color: ${({ theme }) => theme.errorIconColor};
  flex-shrink: 0;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const CloseButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  border-radius: ${({ theme }) => theme.resolve("spacing_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  background: ${({ theme }) => theme.tertiaryButtonBG};
  font-size: ${({ theme }) => theme.fontSizes.regular};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.primaryButtonTextColor};
  cursor: pointer;
`;

const CardContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.regular};
  line-height: ${({ theme }) => theme.resolve("line_height_base")};
  color: ${({ theme }) => theme.primaryTextColor};
  white-space: pre-line;
  overflow-wrap: break-word;
  word-break: break-word;
  min-width: 0;
  max-width: 100%;
`;

const Footer = styled.div`
  padding-top: ${({ theme }) => theme.resolve("spacing_3xl")};
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const ErrorDialog = ({
  show,
  onClose,
  title,
  subtitle,
  icon,
  children,
  primaryButtonLabel,
}: ErrorDialogProps) => {
  const { t } = useTranslation();
  const label = primaryButtonLabel ?? t("Close");

  if (!show) return null;

  return (
    <Modal
      show={show}
      closeModal={onClose}
      overlay
      modalWidth={widthTokens.width_sm}
    >
      <Header>
        <HeaderText>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HeaderText>
      </Header>

      <Card>
        {icon && <IconWrapper>{icon}</IconWrapper>}
        <CardContent>{children}</CardContent>
      </Card>

      <Footer>
        <CloseButton type="button" onClick={onClose}>
          {label}
        </CloseButton>
      </Footer>
    </Modal>
  );
};
